import URI from 'urijs';
import getConfig from 'next/config';

export * from './inputValidation';
export { uploadAvatar, AvatarTypes } from './uploadHelpers';
export { isTouchDevice } from './isTouchDevice';
export * from './linkUtils';
export * from './objectUtils';
export * from './arrayUtils'
export * from './permissionLevels';
export { chunkArrayInDifferentSizes } from './arrayUtils';
export { isSoftKey } from './isSoftKey';
export { md, turndownService, emojiToTwemoji, emojiParse, replaceAll } from './richText';
export { dataURLtoBlob, toDataURL } from './dataUrls';
export { getStripe } from './getStripe';
export { getStringType, capitalizeOrNull, uppercaseOrNull } from './string';
export { shallowEqual } from './shallowEqual';
export { getMeta, isImageEmbeddable } from './imageUtils';
export { ContentTypes, embeddableImageContentTypes, embeddableVideoContentTypes, embeddableContentTypes, lookupFileIconName } from './contentTypes'
export * from './recaptchaHelper';
import { packsClient, templatesClient } from "lib/clients";
import axios from 'axios';
import { decrypt, decryptBinary, decryptPackKey, encrypt, encryptBinary, encryptSessionKey, generateSessionKey } from 'lib/e2ee/helper';
import { generate } from 'generate-password-browser';
import mime from 'mime';
import JSZip from 'jszip';
import { Uploader, UserFileUploader } from './uploadHelpers';
export * from './pack';
export * from './embed';

const { publicRuntimeConfig: { baseProtocol, baseDomain, basePort, baseRESTPort } } = getConfig();

export function baseRESTUrl() {
  if (typeof window === 'undefined') return;

  return window.location.protocol + '//' + window.location.hostname + baseRESTPort;
}

export function baseWSUrl() {
  if (typeof window === 'undefined') return;

  const protocal = baseProtocol === 'https' ? 'wss' : 'ws';

  return protocal + '://' + window.location.hostname + baseRESTPort;
}

export function teamAppUrl(identifier: string): string {
  return `${baseProtocol}://${identifier}.${baseDomain}${basePort}`;
}

export function teamRESTUrl(identifier: string): string {
  return `${baseProtocol}://${identifier}.${baseDomain}${baseRESTPort}`;
}

export function buildBaseUrl(href: string, baseUrl: string) {
  const uri = new URI(href);
  let subdomain = uri.subdomain();

  if (subdomain.endsWith('.staging')) {
    subdomain = subdomain.substring(0, subdomain.indexOf('.staging'));
  }

  const { path } = URI.parse(baseUrl);

  let url = `${baseProtocol}://`;

  if (subdomain && subdomain !== 'staging') {
    url += `${subdomain}.`;
  }

  url += `${baseDomain}${baseRESTPort}${path}`;

  return url;
}

export const retryCommand = (id: string, asset: any, callback: Function) => {
  setTimeout(async () => {
    try {
      await packsClient.createAsset(id, asset);
      callback();
    } catch (err) {

      if (err.response?.status === 402) {
        retryCommand(id, asset, callback);
      }
    }
  }, 10000)
}

export const download = (path: string, filename?: string) => {
  // Create a new link
  const anchor = document.createElement('a');
  anchor.href = path;
  anchor.download = filename;

  // Append to the DOM
  document.body.appendChild(anchor);

  // Trigger `click` event
  anchor.click();

  // Remove element from DOM
  document.body.removeChild(anchor);
};

export const retrieveEncFile = async (packId: string, assetId: string, type: string, sessionKey: any) => {
  const url = `${baseRESTUrl()}/v1/packs/${packId}/files/${assetId}`

  try {
    const response = await axios.get(url, {
        responseType: 'arraybuffer',
        withCredentials: true
    });

    const data = new Uint8Array(response.data);

    const clear = await decryptBinary(data, sessionKey) as any;
    const blob = new Blob([clear], { type });

    return window.URL.createObjectURL(blob);
  }
  catch(e) {}

  return null;
}

export const copyPack = async (pack: any) => {
  const { data } = await packsClient.copyPack(pack.id);

  return data;
}

export const createEncryptedPack = async (title: string, keys: any) => {
  const shareKeys = [
    generate({ length: 32, numbers: true, excludeSimilarCharacters: false, symbols: false }),
    generate({ length: 32, numbers: true, excludeSimilarCharacters: false, symbols: false }),
    generate({ length: 32, numbers: true, excludeSimilarCharacters: false, symbols: false })
  ]

  const [sessionKey, key] = await generateSessionKey(keys.public, shareKeys);

  const share_keys = [];

  for (const shareKey of shareKeys) {
    share_keys.push(await encrypt(shareKey, sessionKey));
  }

  const { data } = await packsClient.createPack({ title, key_id: keys.id, key, share_keys })

  data.key = sessionKey;

  return data;
}

const exportPack = async (packId: string) => {
  const { data } = await packsClient.exportPack(packId);

  const file = new Blob([data], { type: 'application/zip' });

  const jszip = new JSZip();
  return await jszip.loadAsync(file);
}

const reEncryptPack = async (zipFile: any, oldKey: string, keys: any, numOfShareKeys: number) => {
  const packStr = await zipFile.file('pack.json').async('string');
  const pack = JSON.parse(packStr);

  const shareKeys = [];

  for (let x = 0; x < numOfShareKeys; x++) {
    const shareKey = generate({ length: 32, numbers: true, excludeSimilarCharacters: false, symbols: false });
    shareKeys.push(shareKey);
  }

  const [sessionKey, key] = await generateSessionKey(keys.public, shareKeys);

  const share_keys = [];

  for (const shareKey of shareKeys) {
    share_keys.push(await encrypt(shareKey, sessionKey));
  }

  pack.key = key;
  pack.key_id = keys.id;
  pack.share_keys = share_keys;

  for (const asset of pack.assets) {
    const data = await decrypt(asset.enc_data, oldKey);

    if (asset.type === 'file') {
      const encFileData = await zipFile.file(asset.id).async('uint8array');

      const fileData = await decryptBinary(encFileData, oldKey);

      const newFileData = await encryptBinary(fileData, sessionKey) as Uint8Array;

      zipFile.file(asset.id, newFileData);
    }

    asset.enc_data = await encrypt(data, sessionKey);
  }

  const updatedPack = JSON.stringify(pack);

  zipFile.file('pack.json', updatedPack);

  return await zipFile.generateAsync({ type: 'blob' });
}

const importPack = async (zipFile: any, action?: string) => {
  const userUploader = new UserFileUploader();

  const uploader = new Uploader(userUploader, false, [zipFile], (percent: number) => {
    console.log(`Uploading ${percent}`);
  });

  const response = await uploader.upload();

  const upload_token = response.files[0].token;

  const { data } = await packsClient.importPack(upload_token, action === 'to_templates', action === 'from_templates');

  return data;
}

export const clientSideTemplatizePack = async (pack: any, keys: any) => {
  const zipFile = await exportPack(pack.id);

  const newZip = await reEncryptPack(zipFile, pack.key, keys, 1);

  return await importPack(newZip, 'to_templates');
}

export const clientSideCopyPack = async (pack: any, keys: any) => {
  const zipFile = await exportPack(pack.id);

  const newZip = await reEncryptPack(zipFile, pack.key, keys, 3);

  return await importPack(newZip, 'to_packs');
}

export const templatizePack = async (pack: any) => {
  const { data } = await packsClient.templatize(pack.id);

  return data;
}

export const clientSideCreatePackFromTemplate = async (template: any, keys: any) => {
  const packKey = await decryptPackKey(template, keys);

  if (!packKey) {
    throw Error('Could not decrypt key');
  }

  const zipFile = await exportPack(template.id);

  const newZip = await reEncryptPack(zipFile, packKey, keys, 3);

  return await importPack(newZip, 'from_templates');
}

export const createPackFromTemplate = async (pack: any) => {
  const { data } = await templatesClient.createPackFromTemplate(pack.id);

  return data;
}

export const urlToContentType = (url: string) => {
  const uri = new URI(url);
  const suffix = uri.suffix();

  return mime.getType(suffix);
}
